import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { pdfjs } from 'react-pdf';
import {
  X,
  Loader2,
  AlertCircle,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Minus,
  Square,
  ExternalLink,
  Download,
} from 'lucide-react';
import toast from 'react-hot-toast';
import documentService from '../../services/document.service';
import { ROUTES } from '../../config/routes.config';
import VirtualizedPdf from '../pdf/VirtualizedPdf';

const pdfWorkerPublicPath = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/pdf.worker.min.js`;
if (typeof window !== 'undefined' && pdfjs.GlobalWorkerOptions.workerSrc !== pdfWorkerPublicPath) {
  pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerPublicPath;
}

/** Above AppShell sidebar (40) and mobile drawer overlay/sidebar (998/999). */
const PREVIEW_MODAL_Z = 1100;

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

/** Resolved file category after preview loads; used to hide row-level Download for PDF/image while open. */
export type PreviewContentKind = 'pdf' | 'image' | 'other';

function mimeToPreviewContentKind(mime: string): PreviewContentKind {
  const m = (mime || '').toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m === 'application/pdf' || m.includes('pdf')) return 'pdf';
  return 'other';
}

interface DocumentPreviewModalProps {
  documentId: number | string | null;
  documentName?: string;
  onClose: () => void;
  onRequestAccess?: (documentId: string) => void;
  /** Fired when MIME is known after load, and `null` while loading or after close/unmount. */
  onPreviewContentKind?: (kind: PreviewContentKind | null) => void;
}

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  documentId,
  documentName,
  onClose,
  onRequestAccess,
  onPreviewContentKind,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blobMimeType, setBlobMimeType] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; accessDenied: boolean } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [privacyMask, setPrivacyMask] = useState(false);
  const [zoomLoading, setZoomLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const blobUrlRef = useRef<string | null>(null);
  // Page-level retry logic used to exist here; virtualization eliminated the
  // most common blank/black canvas causes (too many simultaneous renders).
  const onPreviewContentKindRef = useRef(onPreviewContentKind);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pdfPageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const panRef = useRef({ active: false, x: 0, y: 0, sl: 0, st: 0, pointerId: -1 });
  onPreviewContentKindRef.current = onPreviewContentKind;

  const id = documentId != null ? String(documentId).trim() : '';
  const resolvedDocumentName = (documentName || `Document-${id}`).trim();

  const handleClose = () => {
    setIsFullscreen(false);
    onClose();
  };

  const handleDownload = async () => {
    if (!id || id === 'NaN' || id === 'undefined' || downloading) return;
    setDownloading(true);
    try {
      await documentService.downloadDocument(id, resolvedDocumentName);
    } catch (err: any) {
      toast.error(err?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenInNewTab = async () => {
    if (!id || id === 'NaN' || id === 'undefined') return;
    const viewerPath = ROUTES.DOCUMENT_VIEWER;
    const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const viewerUrl = `${window.location.origin}${publicBase}${viewerPath}`;
    const win = window.open('about:blank', '_blank');
    if (!win) return;
    try {
      const frameSrc = `${viewerUrl}/${encodeURIComponent(id)}?name=${encodeURIComponent(
        resolvedDocumentName
      )}`;
      const safeTitle = resolvedDocumentName
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      const safeFrameSrc = frameSrc
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      win.document.open();
      win.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${safeTitle}</title>
    <style>
      html, body { width: 100%; height: 100%; margin: 0; background: #111827; }
      iframe { width: 100%; height: 100%; border: 0; display: block; }
    </style>
  </head>
  <body>
    <iframe src="${safeFrameSrc}" title="${safeTitle}" referrerpolicy="no-referrer"></iframe>
  </body>
</html>`);
      win.document.close();
    } catch {
      window.open(`${viewerUrl}/${encodeURIComponent(id)}`, '_blank');
    }
  };

  useEffect(() => {
    setZoom(1);
    setPdfNumPages(0);
    setCurrentPage(1);
    setPageInputValue('1');
    pdfPageRefs.current = {};
  }, [id]);

  useLayoutEffect(() => {
    if (!blobUrl || loading) return;
    scrollRef.current?.scrollTo({ left: 0, top: 0 });
  }, [id, blobUrl, loading]);

  useEffect(() => {
    return () => {
      onPreviewContentKindRef.current?.(null);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    if (!id || id === 'NaN' || id === 'undefined') return;

    const loadStartTime = performance.now();
    console.log('[Load] Document loading started at:', loadStartTime, 'Document ID:', id);
    setLoading(true);
    setError(null);
    setBlobUrl(null);
    setBlobMimeType('');
    onPreviewContentKindRef.current?.(null);
    documentService
      .getFileForPreview(id)
      .then(({ url, mimeType }) => {
        const loadEndTime = performance.now();
        console.log('[Load] Document fetch completed at:', loadEndTime, 'Duration:', (loadEndTime - loadStartTime).toFixed(2), 'ms', 'MIME type:', mimeType);
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        blobUrlRef.current = url;
        setBlobUrl(url);
        setBlobMimeType(mimeType);
        onPreviewContentKindRef.current?.(mimeToPreviewContentKind(mimeType));
        setLoading(false);
        console.log('[Load] Document loading completed at:', performance.now(), 'Total duration:', (performance.now() - loadStartTime).toFixed(2), 'ms');
      })
      .catch((err) => {
        const loadEndTime = performance.now();
        console.log('[Load] Document loading FAILED at:', loadEndTime, 'Duration:', (loadEndTime - loadStartTime).toFixed(2), 'ms', 'Error:', err?.message);
        onPreviewContentKindRef.current?.(null);
        const message = err?.message || 'Failed to load preview';
        const accessDenied =
          err?.status === 403 ||
          /permission|access/i.test(String(message || ''));
        setError({ message, accessDenied });
        setLoading(false);
      });

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      onPreviewContentKindRef.current?.(null);
    };
  }, [id]);

  const isImagePreview = blobMimeType.startsWith('image/');
  const isPdfPreview = mimeToPreviewContentKind(blobMimeType) === 'pdf';
  const pdfFile = useMemo(() => (blobUrl && isPdfPreview ? blobUrl : null), [blobUrl, isPdfPreview]);
  const totalPages = isPdfPreview ? pdfNumPages : isImagePreview ? 1 : 0;
  const canNavigatePages = isPdfPreview && totalPages > 1;
  const totalPageDigits = Math.max(1, String(totalPages || 1).length);
  const pageBoxWidthPx = Math.max(76, totalPageDigits * 14 + 28);
  const totalBoxWidthPx = Math.max(54, totalPageDigits * 12 + 22);

  useEffect(() => {
    if (totalPages <= 0) return;
    setCurrentPage((prev) => {
      const clamped = Math.min(Math.max(prev, 1), totalPages);
      return clamped;
    });
  }, [totalPages]);

  useEffect(() => {
    if (totalPages <= 0) {
      setPageInputValue('1');
      return;
    }
    setPageInputValue(String(currentPage));
  }, [currentPage, totalPages]);

  useEffect(() => {
    const handlePreviewShortcutBlock = (e: KeyboardEvent) => {
      const key = String(e.key || '').toLowerCase();
      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      const isPrintShortcut = ctrlOrMeta && key === 'p';
      const isSaveShortcut = ctrlOrMeta && key === 's';
      const isPrintScreen = key === 'printscreen';
      const isWindowsSnip = ctrlOrMeta && e.shiftKey && key === 's';
      const isMacScreenCapture =
        e.metaKey && e.shiftKey && (key === '3' || key === '4' || key === '5');

      if (isPrintScreen || isWindowsSnip || isMacScreenCapture) {
        setPrivacyMask(true);
        window.setTimeout(() => setPrivacyMask(false), 1200);
      }

      if (
        isPrintShortcut ||
        isSaveShortcut ||
        isPrintScreen ||
        isWindowsSnip ||
        isMacScreenCapture
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('keydown', handlePreviewShortcutBlock, true);
    window.addEventListener('keyup', handlePreviewShortcutBlock, true);
    return () => {
      window.removeEventListener('keydown', handlePreviewShortcutBlock, true);
      window.removeEventListener('keyup', handlePreviewShortcutBlock, true);
    };
  }, []);

  useEffect(() => {
    let unmaskTimer: number | null = null;
    const mask = () => setPrivacyMask(true);
    const unmask = () => {
      if (unmaskTimer) window.clearTimeout(unmaskTimer);
      unmaskTimer = window.setTimeout(() => setPrivacyMask(false), 250);
    };
    const handleVisibility = () => {
      if (document.hidden) mask();
      else unmask();
    };
    window.addEventListener('blur', mask);
    window.addEventListener('focus', unmask);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      if (unmaskTimer) window.clearTimeout(unmaskTimer);
      window.removeEventListener('blur', mask);
      window.removeEventListener('focus', unmask);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  if (!id || id === 'NaN' || id === 'undefined') return null;

  const blockContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const blockMiddleClick = (e: React.MouseEvent) => {
    if (e.button === 1) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const zoomIn = () => {
    const startTime = performance.now();
    console.log('[Zoom] Zoom IN started at:', startTime, 'Current zoom:', zoom);
    setZoomLoading(true);
    setZoom((z) => {
      const newZoom = Math.min(MAX_ZOOM, Math.round((z + ZOOM_STEP) * 100) / 100);
      console.log('[Zoom] Zoom IN new level:', newZoom);
      return newZoom;
    });
    setTimeout(() => {
      const endTime = performance.now();
      console.log('[Zoom] Zoom IN completed at:', endTime, 'Duration:', (endTime - startTime).toFixed(2), 'ms');
      setZoomLoading(false);
    }, 500);
  };
  const zoomOut = () => {
    const startTime = performance.now();
    console.log('[Zoom] Zoom OUT started at:', startTime, 'Current zoom:', zoom);
    setZoomLoading(true);
    setZoom((z) => {
      const newZoom = Math.max(MIN_ZOOM, Math.round((z - ZOOM_STEP) * 100) / 100);
      console.log('[Zoom] Zoom OUT new level:', newZoom);
      return newZoom;
    });
    setTimeout(() => {
      const endTime = performance.now();
      console.log('[Zoom] Zoom OUT completed at:', endTime, 'Duration:', (endTime - startTime).toFixed(2), 'ms');
      setZoomLoading(false);
    }, 500);
  };
  const zoomReset = () => {
    const startTime = performance.now();
    console.log('[Zoom] Zoom RESET started at:', startTime, 'Current zoom:', zoom);
    setZoomLoading(true);
    setZoom(1);
    requestAnimationFrame(() => scrollRef.current?.scrollTo({ left: 0, top: 0 }));
    setTimeout(() => {
      const endTime = performance.now();
      console.log('[Zoom] Zoom RESET completed at:', endTime, 'Duration:', (endTime - startTime).toFixed(2), 'ms');
      setZoomLoading(false);
    }, 500);
  };

  const goToPage = (requestedPage: number) => {
    if (!isPdfPreview || totalPages <= 0) return;
    const next = Math.min(Math.max(requestedPage, 1), totalPages);
    setCurrentPage(next);
    setPageInputValue(String(next));
    requestAnimationFrame(() => {
      const container = scrollRef.current;
      const target = pdfPageRefs.current[next];
      if (!container || !target) return;
      container.scrollTo({
        left: 0,
        top: Math.max(0, target.offsetTop - 8),
        behavior: 'smooth',
      });
    });
  };

  const handlePageInputCommit = () => {
    if (!isPdfPreview || totalPages <= 0) return;
    const parsed = Number.parseInt(pageInputValue, 10);
    if (Number.isNaN(parsed)) {
      setPageInputValue(String(currentPage));
      return;
    }
    goToPage(parsed);
  };

  const endPan = (e: React.PointerEvent) => {
    if (!panRef.current.active) return;
    panRef.current.active = false;
    setIsPanning(false);
    const el = scrollRef.current;
    if (el && panRef.current.pointerId === e.pointerId) {
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    panRef.current.pointerId = -1;
  };

  const onPanPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    if (e.button !== 0) return;
    const el = scrollRef.current;
    if (!el) return;
    panRef.current = {
      active: true,
      x: e.clientX,
      y: e.clientY,
      sl: el.scrollLeft,
      st: el.scrollTop,
      pointerId: e.pointerId,
    };
    setIsPanning(true);
    el.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const onPanPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!panRef.current.active || !scrollRef.current) return;
    const el = scrollRef.current;
    const dx = e.clientX - panRef.current.x;
    const dy = e.clientY - panRef.current.y;
    el.scrollLeft = panRef.current.sl - dx;
    el.scrollTop = panRef.current.st - dy;
  };

  const handlePdfScroll = () => {
    if (!isPdfPreview || pdfNumPages <= 0 || !scrollRef.current) return;
    const markerY = scrollRef.current.scrollTop + 80;

    // Binary search O(log n) — O(n) linear scan was a bottleneck for large PDFs
    // (2000 pages × 60 fps scroll = 120 000 iterations/sec with linear scan).
    let lo = 1, hi = pdfNumPages, visiblePage = 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const el = pdfPageRefs.current[mid];
      if (el && el.offsetTop <= markerY) {
        visiblePage = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    if (visiblePage !== currentPage) setCurrentPage(visiblePage);
  };

  const scrollSurfaceBase =
    'h-full w-full overflow-auto select-none touch-pan-x touch-pan-y ' +
    (isPanning ? 'cursor-grabbing' : 'cursor-grab');
  const imageScrollClass = `${scrollSurfaceBase} bg-[var(--color-gray-100)]`;
  const pdfScrollClass = `${scrollSurfaceBase} bg-[var(--color-gray-200)]`;

  const modal = (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/45 p-4 sm:p-6"
      style={{ zIndex: PREVIEW_MODAL_Z }}
      role="dialog"
      aria-modal="true"
      aria-label="Document preview"
      onClick={handleClose}
      onContextMenu={blockContextMenu}
      onAuxClick={blockMiddleClick}
    >
      <div
        className={
          isFullscreen
            ? 'flex h-screen w-screen max-w-none flex-col overflow-hidden bg-[var(--bg-card)]'
            : 'flex h-[88vh] w-full max-w-[1200px] flex-col overflow-hidden rounded-xl border border-[var(--color-gray-200)] bg-[var(--bg-card)] shadow-2xl'
        }
        onClick={(e) => e.stopPropagation()}
        onContextMenu={blockContextMenu}
        onAuxClick={blockMiddleClick}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[var(--color-gray-200)] px-4 py-2.5">
          <span className="text-md font-medium text-[var(--color-gray-800)]">Document Preview</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsFullscreen(false)}
              disabled={!isFullscreen}
              className="rounded-md p-1.5 text-[var(--color-gray-600)] hover:bg-[var(--color-gray-100)] disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Minimize"
              title="Minimize"
            >
              <Minus size={18} />
            </button>
            <button
              type="button"
              onClick={() => setIsFullscreen(true)}
              disabled={isFullscreen}
              className="rounded-md p-1.5 text-[var(--color-gray-600)] hover:bg-[var(--color-gray-100)] disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Maximize"
              title="Maximize"
            >
              <Square size={15} />
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md p-1.5 text-[var(--color-gray-600)] hover:bg-[var(--color-gray-100)]"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      {blobUrl && !loading && !error && (
        <div
          className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[var(--color-gray-100)] bg-[var(--color-gray-50)] px-4 py-2"
          onContextMenu={blockContextMenu}
        >
          <span className="text-xs font-medium text-[var(--color-gray-600)]">Zoom</span>
          <button
            type="button"
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="inline-flex items-center justify-center rounded-md border border-[var(--color-gray-200)] bg-white p-1.5 text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Zoom out"
            title="Zoom out"
          >
            <ZoomOut size={16} />
          </button>
          <span className="min-w-[3rem] text-center text-xs font-semibold tabular-nums text-[var(--color-gray-800)]">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="inline-flex items-center justify-center rounded-md border border-[var(--color-gray-200)] bg-white p-1.5 text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Zoom in"
            title="Zoom in"
          >
            <ZoomIn size={16} />
          </button>
          <button
            type="button"
            onClick={zoomReset}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-gray-200)] bg-white px-2 py-1 text-xs font-medium text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)]"
            aria-label="Reset zoom"
            title="Reset zoom and pan"
          >
            <RotateCcw size={14} />
            Reset
          </button>
          <span className="text-[11px] text-[var(--color-gray-500)]">
            Drag to pan · Scroll wheel to move
          </span>
          {totalPages > 0 && (
            <>
              <span className="mx-1 h-4 w-px bg-[var(--color-gray-300)]" />
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={!canNavigatePages || currentPage <= 1}
                className="inline-flex items-center justify-center rounded-md border border-[var(--color-gray-200)] bg-white p-1.5 text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)] disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Previous page"
                title="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="inline-flex items-center gap-1 text-xs text-[var(--color-gray-700)]">
                <input
                  type="text"
                  inputMode="numeric"
                  value={pageInputValue}
                  onChange={(e) =>
                    setPageInputValue(e.target.value.replace(/[^\d]/g, ''))
                  }
                  onBlur={handlePageInputCommit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handlePageInputCommit();
                    }
                  }}
                  disabled={!isPdfPreview || totalPages <= 0}
                  className="rounded-md border border-[var(--color-gray-200)] bg-white px-2 py-1 text-center text-xs font-semibold tabular-nums text-[var(--color-gray-800)] outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    width: `${pageBoxWidthPx}px`,
                    minWidth: `${pageBoxWidthPx}px`,
                    paddingLeft: '6px',
                    paddingRight: '6px',
                    lineHeight: 1.2,
                    letterSpacing: 0,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                  aria-label="Current page"
                />
                <span>/</span>
                <span
                  className="rounded bg-white px-1 text-center font-semibold tabular-nums text-[var(--color-gray-800)]"
                  style={{ minWidth: `${totalBoxWidthPx}px` }}
                >
                  {totalPages}
                </span>
              </div>
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={!canNavigatePages || currentPage >= totalPages}
                className="inline-flex items-center justify-center rounded-md border border-[var(--color-gray-200)] bg-white p-1.5 text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)] disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Next page"
                title="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-[var(--color-gray-200)] bg-white px-2 py-1 text-xs font-medium text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Download"
            title="Download"
          >
            {downloading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            Download
          </button>
          <button
            type="button"
            onClick={handleOpenInNewTab}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-gray-200)] bg-white px-2 py-1 text-xs font-medium text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)]"
            aria-label="Open in new tab"
            title="Open in new tab"
          >
            <ExternalLink size={14} />
            Open
          </button>
        </div>
      )}
        <div className="relative min-h-0 flex-1 overflow-hidden">
        {privacyMask && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-black text-sm font-medium text-white">
            Protected preview
          </div>
        )}
        {loading && (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-[var(--color-gray-600)]">
            <Loader2 size={20} className="animate-spin" />
            Loading preview…
          </div>
        )}
        {error && !loading && (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-sm text-[var(--color-error)]">
            <AlertCircle size={24} />
            <p>{error.message}</p>
            {error.accessDenied && onRequestAccess && (
              <button
                type="button"
                onClick={() => onRequestAccess(id)}
                className="mt-2 rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-primary-light)]"
              >
                Send Request Access
              </button>
            )}
          </div>
        )}
        {blobUrl && !loading && !error && isImagePreview && (
          <div
            ref={scrollRef}
            className={imageScrollClass}
            onContextMenu={blockContextMenu}
            onAuxClick={blockMiddleClick}
            onPointerDown={onPanPointerDown}
            onPointerMove={onPanPointerMove}
            onPointerUp={endPan}
            onPointerCancel={endPan}
          >
            <div className="inline-block min-w-full p-4 flex items-center justify-center">
              <img
                src={blobUrl}
                alt="Document preview"
                draggable={false}
                className="max-w-none object-contain"
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'center center',
                  maxHeight: zoom <= 1 ? 'calc(100vh - 140px)' : undefined,
                }}
                onContextMenu={blockContextMenu}
                onDragStart={(e) => e.preventDefault()}
              />
            </div>
          </div>
        )}
        {blobUrl && !loading && !error && isPdfPreview && pdfFile && (
          <div
            ref={scrollRef}
            className={pdfScrollClass}
            onScroll={handlePdfScroll}
            onContextMenu={blockContextMenu}
            onAuxClick={blockMiddleClick}
            onPointerDown={onPanPointerDown}
            onPointerMove={onPanPointerMove}
            onPointerUp={endPan}
            onPointerCancel={endPan}
            data-pdf-scroll-root="true"
          >
            {zoomLoading && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/20">
                <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-lg">
                  <Loader2 size={18} className="animate-spin text-[var(--color-primary)]" />
                  <span className="text-sm font-medium text-[var(--color-gray-700)]">Applying zoom…</span>
                </div>
              </div>
            )}
            <div className="inline-flex min-h-full min-w-full flex-col items-center gap-4 p-4">
              <VirtualizedPdf
                file={pdfFile}
                scale={zoom}
                pageClassName="shadow-sm"
                currentPage={currentPage}
                onLoadSuccess={({ numPages: n }) => {
                  setPdfNumPages(n);
                  setCurrentPage((prev) => Math.min(Math.max(prev, 1), n || 1));
                }}
                onLoadError={() => {
                  setPdfNumPages(0);
                  setCurrentPage(1);
                  setPageInputValue('1');
                }}
                onPageWrapperRef={(pageNumber, el) => {
                  pdfPageRefs.current[pageNumber] = el;
                }}
                onCurrentPageChange={(page) => {
                  if (page !== currentPage) setCurrentPage(page);
                }}
                config={{ rootMarginPx: 600, estimatedPageHeightPx: 1100 }}
              />
            </div>
          </div>
        )}
        {blobUrl && !loading && !error && !isImagePreview && !isPdfPreview && (
          <div
            ref={scrollRef}
            className={pdfScrollClass}
            onContextMenu={blockContextMenu}
            onAuxClick={blockMiddleClick}
            onPointerDown={onPanPointerDown}
            onPointerMove={onPanPointerMove}
            onPointerUp={endPan}
            onPointerCancel={endPan}
          >
            <div className="inline-block min-w-full p-4 flex items-center justify-center">
              <div
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'center center',
                  minHeight: `${Math.max(100, 85 * zoom)}vh`,
                }}
              >
                <object
                  data={blobUrl}
                  type={blobMimeType || 'application/pdf'}
                  className="block border-0"
                  style={{
                    minHeight: `${Math.max(70, 70 * zoom)}vh`,
                    height: `${Math.max(70, 70 * zoom)}vh`,
                  }}
                >
                  <iframe
                    title="Document preview"
                    src={blobUrl}
                    className="block border-0"
                    style={{
                      minHeight: `${Math.max(70, 70 * zoom)}vh`,
                      height: `${Math.max(70, 70 * zoom)}vh`,
                    }}
                  />
                </object>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default DocumentPreviewModal;
